// frontend/src/components/ProtectedRoute.js

import React, { Suspense, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import BrandBootLoader from "./BrandBootLoader";
import { preloadLoginPage } from "./LazyLoginPage";
import LazyAdminPageSkeleton from "./loading/LazyAdminPageSkeleton";
import LazySettingsPageSkeleton from "./loading/LazySettingsPageSkeleton";
import { isAuthBootActive, releaseAuthBoot } from "../utils/authBoot";

/**
 * The redirect to /login, plus taking the authenticated-boot splash down
 * (public/index.html, backlog #158). A stale `lf_auth` hint with an expired
 * session lands here, and Login renders no AuthLayout - the splash's usual
 * release - so without this it would hang over the login page until App's 3s
 * backstop.
 *
 * The splash comes down only once the Login page's chunk has SETTLED (loaded
 * or failed): released at once, it would fade onto App's loader while the
 * chunk is still on its way. Nothing else waits on it - this component
 * unmounts as soon as <Navigate> has moved to /login, and the release that
 * arrives after that is the intended one (idempotent; nothing else touches
 * state). A chunk that never settles is bounded by App's 3s cap; a failed one
 * reveals at once onto whatever the route's own error path shows.
 *
 * In an effect, not during render: render stays pure (React may render more
 * than once before committing), and both the preload and the release are
 * idempotent, so StrictMode's double effect is harmless. The <Navigate> itself
 * is exactly the one this guard always rendered.
 *
 * @param {object} props
 * @param {object} props.from the location the visitor was headed to
 */
function RedirectToLogin({ from }) {
  useEffect(() => {
    // No splash, nothing to time: a redirect on any later navigation leaves
    // Login's chunk to the route itself, exactly as before.
    if (!isAuthBootActive()) return;
    const release = () => releaseAuthBoot();
    try {
      Promise.resolve(preloadLoginPage()).then(release, release);
    } catch (e) {
      release();
    }
  }, []);
  return <Navigate to="/login" replace state={{ from }} />;
}

/**
 * Takes the authenticated-boot splash down on its first commit. Rendered as
 * the last child of ProtectedPage's boundary, so it commits together with the
 * page - only once the page's lazy chunk has resolved - and never while the
 * branded loader is still standing in. That covers the protected landings
 * with no AuthLayout of their own (RecommenderDashboard, where a pure
 * recommender's cold start ends up; RecommenderEarnings; the client-statement
 * print page), which would otherwise wait for App's 3s cap. On an AuthLayout
 * page it commits in the same pass as the layout's own release - idempotent.
 */
function ReleaseAuthBootOnMount() {
  useEffect(() => {
    releaseAuthBoot();
  }, []);
  return null;
}

/**
 * The cold-boot boundary: the page this guard lets through, under the branded
 * loader while its lazy chunk downloads (instead of App's grey
 * PageLoadingFallback, which public pages keep), so a splash that comes down
 * before the page is ready (the 3s backstop on a slow connection) fades onto
 * the same picture rather than a spinner. AuthLayout is part of `children`,
 * so it still commits - and releases the splash - only once the chunk has
 * resolved, except on /settings, /admin and /admin/users/:userId, where the
 * page sits in loading/PageBodySuspense.js inside AuthLayout and the layout
 * commits first. A route that declares its own inner <Suspense> keeps that one:
 * the nearest boundary wins.
 *
 * Created only for a ProtectedRoute mounted while the boot splash is up
 * (decided once per mount), and kept for that instance afterwards -
 * react-router reuses it across navigations between protected routes, and
 * those navigations are transitions, so the already-revealed boundary keeps
 * the old page on screen. A mount made after the boot gets none: a NEW
 * boundary shows its fallback even inside a transition, so on an in-app
 * navigation (Login -> /dashboard with a cold chunk) it would swap the
 * still-visible page for a full-screen loader; without it the page suspends
 * into App's existing boundary and react-router's transition keeps the old
 * page on screen, as it always did.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
function ProtectedPage({ children }) {
  return (
    <Suspense fallback={<BrandBootLoader />}>
      {children}
      <ReleaseAuthBootOnMount />
    </Suspense>
  );
}

/**
 * Route guard. Mirrors the server-side dependencies in
 * `backend/app/api/deps.py`:
 *
 * - `allowedTypes` mirrors tier checks (trial/professional/enterprise).
 * - `requireRecommender` mirrors `get_current_recommender_user`, which is now
 *   capability-based (`is_recommender`, derived from an active Recommender
 *   profile) — independent of `user_type`. This lets a dual-role user
 *   (e.g. user_type=professional + is_recommender=true) reach BOTH dashboards.
 * - `requireInterventionEnabled` / `requireBIEnabled` are also capability flags,
 *   already independent of user_type.
 * - `requireFlag` gates on a single derived capability flag on the user payload
 *   (e.g. "website_eligible"). Prefer this over widening `allowedTypes` when the
 *   backend already centralizes the tier logic into one boolean — appending the
 *   tier strings here would re-duplicate that logic and drift from the server.
 */
const ProtectedRoute = ({
  children,
  allowedTypes = null,
  requireInterventionEnabled = false,
  requireBIEnabled = false,
  requireAdmin = false,
  requireRecommender = false,
  requireFlag = null,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  // Mounted while the boot splash is up: wrap the page in the cold-boot
  // boundary (ProtectedPage). Decided ONCE, so the tree's shape never changes
  // under a mounted page - react-router reuses this instance across
  // navigations between protected routes, where the already-revealed boundary
  // simply keeps showing the old page during a transition.
  const [bootBoundary] = useState(isAuthBootActive);
  const isSignedIn = !loading && Boolean(isAuthenticated);
  const isAdmin = isSignedIn && Boolean(user?.is_admin);

  // The page-body skeletons are chunks of their own (loading/
  // LazySettingsPageSkeleton.js, loading/LazyAdminPageSkeleton.js), kept out
  // of the main chunk every anonymous visitor downloads. Start them as soon as
  // the session says who this is, so they render with no wait when opened:
  // Settings' for every signed-in visitor (every tier can open /settings), the
  // admin one for admins only (/admin, /admin/users/:id). In an effect, never
  // in render; idempotent and never rejecting; nothing for anyone signed out.
  useEffect(() => {
    if (!isSignedIn) return;
    LazySettingsPageSkeleton.preload();
    if (isAdmin) LazyAdminPageSkeleton.preload();
  }, [isSignedIn, isAdmin]);

  /** The page, inside the cold-boot boundary only when this mount needs one. */
  const page = bootBoundary ? <ProtectedPage>{children}</ProtectedPage> : children;

  // Waiting for the session check: the branded loader, the twin of the boot
  // splash that is usually still covering this frame (BrandBootLoader.js).
  if (loading) {
    return <BrandBootLoader />;
  }

  if (!isAuthenticated) {
    // Preserve where the user was headed (incl. query string like
    // ?lead=<id>) so Login can send them back after authenticating.
    // Without this, a logged-out user opening a shared lead link lands
    // on a bare dashboard instead of the lead they were sent to.
    return <RedirectToLogin from={location} />;
  }

  // Check admin requirement FIRST (before other checks)
  if (requireAdmin && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Recommender capability — derived server-side from an active
  // Recommender profile row, not from user_type. Admin bypasses for support.
  if (requireRecommender && !user?.is_recommender && !user?.is_admin) {
    // No recommender capability → fall back to /dashboard if the user has a
    // tier that can use it; otherwise /login (defensive — this should never
    // happen for an authenticated user).
    return <Navigate to="/dashboard" replace />;
  }

  // Check intervention feature if required (admin can access for testing)
  if (requireInterventionEnabled && !user?.intervention_enabled && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check Business Intelligence feature if required (admin can access for testing).
  // Backend exposes the flag as `has_active_bi_monitor` (computed from BIMonitor.is_active).
  if (requireBIEnabled && !user?.has_active_bi_monitor && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Single derived capability flag (e.g. website_eligible). The real gate is
  // server-side; this is UX-level. Admin bypasses for support/testing.
  if (requireFlag && !user?.[requireFlag] && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  // If no type restriction, allow all authenticated users
  if (!allowedTypes) {
    return page;
  }

  // Admin can access all pages (bypass allowedTypes check)
  if (user?.is_admin) {
    return page;
  }

  // Check if user type is allowed
  if (!allowedTypes.includes(user?.user_type)) {
    // Tier check failed. Decide where to send the user:
    //   - Has the recommender capability → recommender dashboard.
    //   - Otherwise → /dashboard (handles legacy edge cases gracefully).
    // We intentionally check the capability flag instead of `user_type`
    // because dual-role users (e.g. user_type=professional + is_recommender=true)
    // pass `allowedTypes` directly and never reach this branch.
    if (user?.is_recommender) {
      return <Navigate to="/recommender-dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return page;
};

export default ProtectedRoute;
